import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Eye, Shield, BookOpen, Users, Globe, Lightbulb, Heart, ArrowRight } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6
  }
};
export function About() {
  return <div className="pt-0 bg-warmwhite" data-id="element-151" data-mp="3292med0xpbd6">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-navy overflow-hidden" data-id="element-152" data-mp="376cek0fpk14p">
        <div className="absolute inset-0 z-0" data-id="element-153" data-mp="2samqzxuvjfxv">
          <img src="https://images.unsplash.com/photo-1528301721190-186c3bd85418?q=80&w=2940&auto=format&fit=crop" alt="About Poch Global" className="w-full h-full object-cover opacity-30" data-id="element-154" data-mp="2s9v7vmigraoh" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-transparent" data-id="element-155" data-mp="1ltswz19axluw" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center max-w-4xl" data-id="element-156" data-mp="1uha1bljz9ctx">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-157" data-mp="1pj8hgyeq99he">
            About Poch Global
          </h1>
          <p className="text-xl text-white/80 leading-relaxed" data-id="element-158" data-mp="23j9icduzsgwv">
            8+ Years of Connecting Africa with the World Through Education,
            Culture, and Innovation.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-12 lg:py-16" data-id="element-159" data-mp="2deb7h9p9pizg">
        <div className="container mx-auto px-6 md:px-12" data-id="element-160" data-mp="28gi2gjzpav0r">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center" data-id="element-161" data-mp="2m8y9dvvq3d6y">
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="lg:w-1/2 order-2 lg:order-1" data-id="element-162" data-mp="2mb28m0iofo0w">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6" data-id="element-163" data-mp="2w51bj1qkrbtw">
                Our Story
              </h2>
              <div className="space-y-4 md:space-y-6 text-base md:text-lg text-mediumgray leading-relaxed" data-id="element-164" data-mp="1ouwfbh7m1rra">
                <p data-id="element-165" data-mp="1svq38al1z6k6">
                  For over 8 years, Poch Global has been at the forefront of
                  cultural preservation and community empowerment. What started
                  as a passion to connect diaspora communities with their
                  African roots has grown into a comprehensive platform serving
                  thousands globally.
                </p>
                <p data-id="element-166" data-mp="2cjz1919a26rl">
                  We believe that education, culture, and innovation are the
                  keys to unlocking human potential. Our integrated approach
                  touches every aspect of community development—from youth
                  enrichment to health awareness to entrepreneurship.
                </p>
                <p data-id="element-167" data-mp="m92ya03ayn76">
                  As a parent organization, Poch Global oversees multiple
                  initiatives, each designed to address unique needs while
                  maintaining our core mission: connecting Africa with the
                  world.
                </p>
              </div>
            </motion.div>
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="lg:w-1/2 order-1 lg:order-2 w-full" data-id="element-168" data-mp="eutsoio3sm9h">
              <div className="relative rounded-3xl overflow-hidden shadow-premium" data-id="element-169" data-mp="4wmdo5ddkea4">
                <img src="https://images.unsplash.com/photo-1511632765486-a01980e01a18?q=80&w=2940&auto=format&fit=crop" alt="Our Team" className="w-full h-[300px] md:h-[400px] lg:h-auto object-cover" data-id="element-170" data-mp="12k4vs6sxesqs" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 lg:py-20 bg-white border-y border-softgray" data-id="element-171" data-mp="3dflm077pz5ql">
        <div className="container mx-auto px-6 md:px-12" data-id="element-172" data-mp="39bvzdnaluy87">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-173" data-mp="1148xqwb6i7l7">
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-gold/5 p-8 lg:p-10 rounded-3xl border border-gold/10" data-id="element-174" data-mp="1125zxq3sba5x">
              <Target className="w-10 h-10 lg:w-12 lg:h-12 text-gold mb-6" data-id="element-175" data-mp="w4miyifpcjaa" />
              <h3 className="text-xl lg:text-2xl font-bold text-navy mb-4" data-id="element-176" data-mp="w2jjl7sntdsw">Our Mission</h3>
              <p className="text-mediumgray text-base lg:text-lg leading-relaxed" data-id="element-177" data-mp="1jse0q1k0hoiy">
                We connect Africa with the world by empowering people through
                education, leadership, culture, innovation, entrepreneurship,
                awareness, and global partnership.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-navy/5 p-8 lg:p-10 rounded-3xl border border-navy/10" data-id="element-178" data-mp="3vjhx0jf8tvav">
              <Eye className="w-10 h-10 lg:w-12 lg:h-12 text-navy mb-6" data-id="element-179" data-mp="3s12cpmvx1hga" />
              <h3 className="text-xl lg:text-2xl font-bold text-navy mb-4" data-id="element-180" data-mp="3n3ikfj2dv45c">Our Vision</h3>
              <p className="text-mediumgray text-base lg:text-lg leading-relaxed" data-id="element-181" data-mp="5ulvxbg6e811">
                To become Africa's premier global platform concerning Africa and
                friends of Africa worldwide through education, innovation,
                business, culture, and leadership.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-12 lg:py-16" data-id="element-182" data-mp="3j15gn0775gb7">
        <div className="container mx-auto px-6 md:px-12" data-id="element-183" data-mp="3j273s31hlt9a">
          <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16" data-id="element-184" data-mp="3eaytz5w8w5vl">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-4" data-id="element-185" data-mp="3e8ugbw8jnsi4">
              What We Stand For
            </h2>
            <p className="text-mediumgray text-base md:text-lg" data-id="element-186" data-mp="2lmjiuug8i7pj">
              The core principles that guide our work and shape our community.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-187" data-mp="3fbqz6d7xu87e">
            {[{
            title: 'Cultural Preservation',
            icon: Shield,
            desc: 'Protecting and celebrating African heritage for future generations.'
          }, {
            title: 'Educational Excellence',
            icon: BookOpen,
            desc: 'Providing quality learning opportunities that transform lives.'
          }, {
            title: 'Community Empowerment',
            icon: Users,
            desc: 'Building stronger communities from within.'
          }, {
            title: 'Global Connection',
            icon: Globe,
            desc: 'Bridging Africa with the world.'
          }, {
            title: 'Innovation & Growth',
            icon: Lightbulb,
            desc: 'Embracing new ideas and continuous improvement.'
          }, {
            title: 'Inclusive Opportunity',
            icon: Heart,
            desc: 'Creating space for everyone to succeed.'
          }].map((val, i) => <motion.div key={i} variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-white p-8 rounded-3xl shadow-sm border border-softgray text-center" data-id="element-188" data-mp="1rd81xcng6s26" data-mpi={i}>
                <div className="w-16 h-16 mx-auto bg-gold/10 text-gold rounded-full flex items-center justify-center mb-6" data-id="element-189" data-mp="1ddouyn6dhmrg">
                  <val.icon className="w-8 h-8" data-id="element-190" data-mp="18gf7ve6ghn6s" />
                </div>
                <h4 className="text-xl font-bold text-navy mb-3" data-id="element-191" data-mp="37srem48qqamg">
                  {val.title}
                </h4>
                <p className="text-mediumgray" data-id="element-192" data-mp="32u7so3u5cyu3">{val.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Our Impact */}
      <section className="py-12 lg:py-16 bg-navy text-white relative overflow-hidden" data-id="element-193" data-mp="2gmm2d0ojyk7s">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-194" data-mp="22vh0rt1mjhut" />
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-195" data-mp="26wkhsiverjxm">
          <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16" data-id="element-196" data-mp="1sxayhabwo9pf">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-197" data-mp="1svzylllkzm3l">
              By The Numbers
            </h2>
            <p className="text-white/70 text-base md:text-lg" data-id="element-198" data-mp="1jwwau4bgyiir">
              The measurable impact of our community-driven approach.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8 text-center" data-id="element-199" data-mp="1nyhvwcy5q341">
            {[{
            value: '8+',
            label: 'Years of Service'
          }, {
            value: '50+',
            label: 'Programs Delivered'
          }, {
            value: '10k+',
            label: 'Youth Empowered'
          }, {
            value: '100+',
            label: 'Communities Reached'
          }, {
            value: '200+',
            label: 'Events Hosted'
          }, {
            value: '500+',
            label: 'Scholarships Awarded'
          }].map((stat, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-200" data-mp="1gg09x793gvu6" data-mpi={i}>
                <div className="text-4xl md:text-5xl font-serif font-bold mb-2 text-gold" data-id="element-201" data-mp="1zdt7rv4v2fkp">
                  {stat.value}
                </div>
                <div className="text-white/80 font-medium uppercase tracking-wider text-xs md:text-sm" data-id="element-202" data-mp="1ucdn446lib2u">
                  {stat.label}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Our Initiatives */}
      <section className="py-12 lg:py-16 bg-warmwhite" data-id="element-203" data-mp="3cesjwacizlja">
        <div className="container mx-auto px-6 md:px-12" data-id="element-204" data-mp="37f65vyovi19f">
          <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16" data-id="element-205" data-mp="37dle2gty0wf9">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-4" data-id="element-206" data-mp="33ecm9higr8mc">
              Our Initiatives
            </h2>
            <p className="text-mediumgray text-base md:text-lg" data-id="element-207" data-mp="2ygb2eud7r59t">
              Poch Global operates as a parent organization overseeing multiple initiatives, each focused on specific community needs.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-208" data-mp="3mbxbipp5497h">
            {[{
            title: 'Wonder Boy',
            desc: 'Restaurant & Training Center',
            link: '/about'
          }, {
            title: 'Education Programs',
            desc: 'Academic & Tech Enrichment',
            link: '/programs'
          }, {
            title: 'Health Initiatives',
            desc: 'Wellness & Awareness',
            link: '/programs'
          }, {
            title: 'Community Outreach',
            desc: 'Direct Support & Scholarships',
            link: '/programs'
          }, {
            title: 'Events & Workshops',
            desc: 'Cultural & Educational Gatherings',
            link: '/events'
          }].map((init, i) => <motion.div key={i} variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-white p-8 rounded-3xl shadow-sm border border-softgray hover:shadow-premium hover:border-gold/30 transition-all duration-300 group flex flex-col h-full" data-id="element-209" data-mp="v44pbs9ykggw" data-mpi={i}>
                <div className="flex-grow" data-id="element-210" data-mp="1dvnrjww5rqhv">
                  <h3 className="text-xl font-bold text-navy mb-2 group-hover:text-gold transition-colors" data-id="element-211" data-mp="1dwpf30ud25gh">
                    {init.title}
                  </h3>
                  <p className="text-mediumgray mb-6" data-id="element-212" data-mp="1mxna4tmtn2mx">{init.desc}</p>
                </div>
                <Link to={init.link} className="text-navy font-semibold flex items-center gap-2 group-hover:gap-3 transition-all mt-auto" data-id="element-213" data-mp="1rvfbee9cw13s">
                  Learn More <ArrowRight className="w-4 h-4" data-id="element-214" data-mp="3jy1h8hl627k2" />
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-12 lg:py-16 bg-white border-t border-softgray" data-id="element-215" data-mp="3syg8dxktagvh">
        <div className="container mx-auto px-6 md:px-12" data-id="element-216" data-mp="3swd72he7l1bg">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24" data-id="element-217" data-mp="bgdhcsv7pc11">
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="lg:w-5/12 relative w-full" data-id="element-218" data-mp="6jen8abjt52u">
              <div className="relative z-10 rounded-[2rem] overflow-hidden shadow-2xl border border-softgray" data-id="element-219" data-mp="fj1q70jxeiy0">
                <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=2788&auto=format&fit=crop" alt="Funke Akinwunmiju" className="w-full h-[400px] lg:h-[500px] object-cover" data-id="element-220" data-mp="2masrgae4ucc9" />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -left-4 lg:-bottom-6 lg:-left-6 w-32 h-32 lg:w-48 lg:h-48 bg-gold/10 rounded-full blur-2xl -z-10" data-id="element-221" data-mp="11ttd8kxtk039" />
              <div className="absolute -top-4 -right-4 lg:-top-6 lg:-right-6 w-24 h-24 lg:w-32 lg:h-32 border-2 border-gold/20 rounded-full -z-10" data-id="element-222" data-mp="s143pvltwwak" />
            </motion.div>

            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="lg:w-7/12 w-full text-center lg:text-left" data-id="element-223" data-mp="j17mug5ol1q2">
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-navy/5 border border-navy/10 mb-6 lg:mb-8" data-id="element-224" data-mp="92rrg0zmf6ar">
                <span className="w-2 h-2 rounded-full bg-gold" data-id="element-225" data-mp="1zgsdt6rmts6f" />
                <span className="text-xs lg:text-sm font-medium text-navy tracking-wide uppercase" data-id="element-226" data-mp="23gsqalv67tau">
                  Our Leadership
                </span>
              </div>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-navy mb-2 lg:mb-4" data-id="element-227" data-mp="19rbsy50egvzf">
                Funke Akinwunmiju
              </h2>
              <p className="text-lg lg:text-xl text-gold font-medium mb-6 lg:mb-8" data-id="element-228" data-mp="14qnw32phpkxa">
                Founder & Director
              </p>
              
              <div className="space-y-4 lg:space-y-6 text-base lg:text-lg text-mediumgray leading-relaxed mb-8 lg:mb-10 text-left" data-id="element-229" data-mp="vs2fhd3kwfz5">
                <p data-id="element-230" data-mp="r02ah68niwvp">
                  A visionary leader dedicated to bridging the gap between the
                  diaspora and African heritage. Funke founded Poch Global with a
                  deep commitment to education, cultural preservation, and youth
                  empowerment.
                </p>
                <p data-id="element-231" data-mp="c2rzdxzx7t0i">
                  Under her guidance, the organization has grown from a local initiative 
                  into a global platform, touching thousands of lives through innovative 
                  programs like Wonder Boy and extensive community outreach efforts.
                </p>
              </div>

              <div className="flex items-center gap-6 pt-8 border-t border-softgray" data-id="element-232" data-mp="2nsrh51fyviic">
                <div className="flex gap-4" data-id="element-233" data-mp="2snwyro2c6b0m">
                  <a href="#" className="w-10 h-10 rounded-full bg-lightgray flex items-center justify-center text-navy hover:bg-gold hover:text-white transition-all" data-id="element-234" data-mp="2xpcmjnvwevmp">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-235" data-mp="250f3hijv4usw"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-236" data-mp="24ychd9ltzltg" /></svg>
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-lightgray flex items-center justify-center text-navy hover:bg-gold hover:text-white transition-all" data-id="element-237" data-mp="1a6lce6ayrkwx">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-238" data-mp="1j0849lc1w40u"><path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" data-id="element-239" data-mp="1j2ati35dpt4i" /></svg>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-navy text-center text-white relative overflow-hidden" data-id="element-240" data-mp="2r7hu3t0e2nwb">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-241" data-mp="2m8y5fkueuy09" />
        <div className="container mx-auto px-6 relative z-10" data-id="element-242" data-mp="3jvdrkzxs8ju5">
          <h2 className="text-4xl text-white font-serif font-bold mb-8" data-id="element-243" data-mp="3exc50515e2dx">
            Ready to Be Part of Our Story?
          </h2>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-244" data-mp="39x7owg45j9o8">
            <Link to="/programs" className="px-8 py-4 bg-white text-navy hover:bg-warmwhite rounded-full font-bold transition-all shadow-lg hover:-translate-y-1" data-id="element-245" data-mp="35wnsjereka35">
              Explore Programs
            </Link>
            <Link to="/donate" className="px-8 py-4 bg-gold text-white hover:bg-gold-light rounded-full font-bold transition-all shadow-lg hover:-translate-y-1" data-id="element-246" data-mp="1ufxi1jfy4w3q">
              Donate
            </Link>
            <Link to="/contact" className="px-8 py-4 bg-transparent border-2 border-white hover:bg-white hover:text-navy text-white rounded-full font-bold transition-all hover:-translate-y-1" data-id="element-247" data-mp="j04n2ato28ro">
              Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </div>;
}